import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Card,
  Container,
  Row,
  Col,
  HeaderText,
  BodyText,
  TextIconButton,
  Form,
  RotateIcon,
} from 'shared-portal-component';
import { z } from 'zod';
import { useSelector } from 'react-redux';

import '@/components/organism/Registration/AuthFormCommon.scss';
import { useAppDispatch } from '@/store';
import { sendEmailCodeThunk } from '@/store/thunks/auth/sendEmailCodeThunk';
import { resendEmailCodeThunk } from '@/store/thunks/auth/resendEmailCodeThunk';
import { MIN_LEN } from '@/constants';
import { selectTwoFactorMaskedDestination } from '@/store/selectors/auth';
import { confirmEmailThunk } from '@/store/thunks/auth/confirmEmailThunk';

const CODE_LENGTH = 6;

const schema = z.object({
  code: z
    .string()
    .length(CODE_LENGTH, `Code must be exactly ${CODE_LENGTH} digits`)
    .regex(/^\d+$/, 'Code must contain only digits'),
});

interface EmailConfirmationProps {
  formData?: { code?: string };
  onNext: (values: { code: string }) => void;
  resendDisabled?: boolean;
  loading?: boolean;
  error?: string;
}

const EmailConfirmation: React.FC<EmailConfirmationProps> = ({
  formData,
  onNext,
  resendDisabled = false,
  loading,
  error,
}) => {
  const dispatch = useAppDispatch();

  const masked = useSelector(selectTwoFactorMaskedDestination);

  useEffect(() => {
    void dispatch(sendEmailCodeThunk(undefined));
  }, [dispatch]);

  const handleResend = useCallback(() => {
    if (loading === true) return;
    if (resendDisabled) return;
    void dispatch(resendEmailCodeThunk(undefined));
  }, [dispatch, loading, resendDisabled]);

  const handleConfirm = useCallback(
    (values: { code: string }) => {
      if (loading === true) return;

      void (async() => {
        const action = await dispatch(confirmEmailThunk({ code: values.code }));

        if (confirmEmailThunk.fulfilled.match(action)) {
          onNext({ code: values.code });
        }
      })();
    },
    [dispatch, loading, onNext]
  );

  const fields = useMemo(
    () => [
      {
        name: 'code',
        label: 'Code',
        placeholder: 'Enter the 6-digit code',
        required: true,
        variant: 'outline' as const,
        type: 'code',
        defaultValue: formData?.code ?? '',
      },
      {
        type: 'display',
        name: 'resend',
        label: 'resend',
        display: (
          <Row spacing="none" justify="center" className="space-y">
            <TextIconButton
              icon={<RotateIcon />}
              text={resendDisabled ? 'Resend in 30s' : 'Resend'}
              disabled={resendDisabled || loading === true}
              onClick={handleResend}
              variant="outline"
              size="sm"
            />
          </Row>
        ),
      },
    ],
    [formData?.code, handleResend, loading, resendDisabled]
  );

  return (
    <Container fluid>
      <Card>
        <Row spacing="md">
          <Col size={12}>
            <HeaderText text="Confirm email address" size="h2" className="header-text-auth" />
            <BodyText
              text="When you log in, you can choose to have a 6-digit code sent to either your email or your phone (via text message)."
              className="body-text-auth"
              as="p"
            />
            <BodyText
              text={`To make sure it’s working, we’ve sent a code to your email address: ${masked}. Please enter this code below.`}
              className="body-text-auth"
              as="p"
            />

            {typeof error === 'string' && error.trim().length > MIN_LEN && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive mt-3">
                {error}
              </div>
            )}
          </Col>
        </Row>

        <Row justify="center" spacing="none">
          <Form schema={schema} fields={fields} onSubmit={handleConfirm} />
        </Row>
      </Card>
    </Container>
  );
};

export default EmailConfirmation;
